/**
 * @fileoverview Response interceptor para manejo centralizado de errores HTTP
 * Transforma errores de axios en instancias de ApiError y maneja casos especiales
 * Incluye sistema de toasts para feedback visual automático
 * @author Omar Sánchez Martinez
 * @since 15/10/2025
 */

import { useToasts } from '@/common/composables/useToast';
import axios, { type AxiosResponse } from 'axios';
import { formatToastMessage, getErrorMessage, handleApiError } from '../utils';
import { logDebug, logError } from '../utils/logger';

/**
 * Interceptor de respuestas exitosas
 * Simplemente retorna la respuesta sin modificaciones
 *
 * @param response - Respuesta exitosa de axios
 * @returns La misma respuesta sin modificaciones
 *
 * @example
 * // El interceptor se aplica automáticamente
 * const response = await apiMunzen.get('/endpoint');
 * // response.data contiene los datos del servidor
 */
export const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
  // Obtener Request ID del header
  const requestId = response.config.headers['X-Request-ID'] as string | undefined;

  // Log de respuesta exitosa
  logDebug('Respuesta exitosa', {
    requestId,
    method: response.config.method?.toUpperCase(),
    url: response.config.url,
    status: response.status,
  });

  return response;
};

/**
 * Manejador de errores del response interceptor
 * Transforma errores de axios en instancias de ApiError estructuradas
 * Muestra toasts automáticos basados en flags del API
 *
 * Funcionalidad:
 * - Transforma errores a ApiError con información estructurada
 * - Maneja errores 401: Logout automático + redirect + toast con mensaje del API
 * - Maneja errores 403: Redirect a página prohibida (sin toast)
 * - Maneja errores 404: Redirect a página no encontrada (sin toast)
 * - Maneja errores de red: Mensaje de conexión + toast
 * - Maneja errores 500+: Mensaje genérico de servidor + toast
 * - Toasts persistentes con UUID para errores graves
 * - NO muestra toasts para requests cancelados
 *
 * @param error - Error de axios
 * @returns Promise rechazada con ApiError
 *
 * @example
 * try {
 *   await apiMunzen.get('/endpoint');
 * } catch (error) {
 *   // error es una instancia de ApiError
 *   if (isApiError(error)) {
 *     console.log(error.dataError);
 *     console.log(error.status);
 *     console.log(error.errorTrackingUUID);
 *   }
 * }
 */
export const responseInterceptorError = (error: unknown): Promise<never> => {
  // Obtener Request ID si está disponible
  const requestId =
    axios.isAxiosError(error) && error.config?.headers
      ? (error.config.headers['X-Request-ID'] as string | undefined)
      : undefined;

  // Verificar si es request cancelado - NO mostrar toast
  if (axios.isCancel(error)) {
    logDebug('Request cancelado por el cliente', { requestId });
    return Promise.reject(error);
  }

  // Transformar error a ApiError
  const apiError = handleApiError(error);

  // Obtener instancia de toasts
  const { errorToast, persistentErrorToast } = useToasts();

  // Log estructurado del error
  logError('Error en respuesta HTTP', {
    requestId,
    method: axios.isAxiosError(error) ? error.config?.method?.toUpperCase() : undefined,
    url: axios.isAxiosError(error) ? error.config?.url : undefined,
    status: apiError.status,
    data: {
      message: apiError.dataError,
      trackingUUID: apiError.errorTrackingUUID,
      showToClient: apiError.showErrorToClient,
      persistent: apiError.persistentErrorMessage,
    },
    error,
  });

  // Casos especiales donde NO mostrar toast
  // 403: Redirect a forbidden
  // 404: Redirect a notFound
  // Offline: Ya se muestra el banner de PwaOfflineIndicator
  const noToastStatuses = [403, 404];

  if (!noToastStatuses.includes(apiError.status)) {
    // 1. Error persistente con UUID (errores graves 500+, network)
    if (apiError.persistentErrorMessage) {
      let message = getErrorMessage(apiError);

      // Agregar UUID si está disponible
      if (apiError.errorTrackingUUID) {
        message += `<br><br>Hemos sido notificados del problema y lo revisaremos para solucionarlo lo antes posible.<br>Puedes ponerte en contacto con nuestro soporte técnico proporcionando el siguiente ID de error: <strong>${apiError.errorTrackingUUID}</strong>.`;
      }

      // Formatear HTML
      const formattedMessage = formatToastMessage(message);

      persistentErrorToast(apiError.title || 'Error del Servidor', formattedMessage, 22000);
    }
    // 2. Error normal con showErrorToClient (incluye 401 con mensaje específico del API)
    else if (apiError.showErrorToClient) {
      const message = getErrorMessage(apiError);
      const formattedMessage = formatToastMessage(message);

      errorToast(apiError.title || 'Error', formattedMessage);
    }
  }

  // Rechazar con ApiError
  return Promise.reject(apiError);
};
